/**
 * Özet (panel, read only, v0.1.175): GET /ozet. Sercan's one page: what waits for him and a few honest counts, the way
 * the 11 Oct plan review cut it down. Counts, not percentages; the old site (before the 5 Oct launch) and the new site
 * side by side over windows of the same length; a number with nothing to compare shows as a first measurement, and
 * what cannot be measured yet says so instead of showing zero.
 */
import { z } from 'zod';
import { countSchema, isoDateSchema, isoDateTimeSchema } from '../ids.js';
import { SEO_ENGINES } from './seo-report.js';
import { SITE_SOURCE_GROUPS } from './site-stats.js';
/** The day the new cadgaragepro.com went live; search numbers are split here. */
export const SITE_LAUNCH_DAY = '2026-10-05';
/** At most this many waiting items are listed; the rest are counted. */
export const OZET_DECISION_LIMIT = 3;
export const ozetDecisionSchema = z.strictObject({
    code: z.string().min(1).max(80),
    titleTr: z.string().min(1).max(300),
    placeTr: z.string().max(200).nullable(),
    ageDays: countSchema,
});
export const ozetSearchWindowSchema = z.strictObject({
    from: isoDateSchema,
    to: isoDateSchema,
    days: countSchema,
    impressions: countSchema,
    clicks: countSchema,
});
export const ozetSearchSchema = z.strictObject({
    engine: z.enum(['google', 'bing']),
    /** The last day the engine has reported; null before the first read. */
    dataThrough: isoDateSchema.nullable(),
    /** From the launch to dataThrough; null when no day after the launch is in yet. */
    newSite: ozetSearchWindowSchema.nullable(),
    /** The same number of days right before the launch (the old site); null when those days are not stored. */
    oldSite: ozetSearchWindowSchema.nullable(),
});
export const ozetVisitsSchema = z.strictObject({
    /** The server log exists only for the new site, so there is no before. */
    from: isoDateSchema.nullable(),
    to: isoDateSchema.nullable(),
    days: countSchema,
    /** Page views by people arriving from outside the site. */
    entries: countSchema,
    /** Of those, arrivals from an AI assistant's link. */
    aiEntries: countSchema,
    /** Requests from crawlers, AI crawlers included: reading, not visiting. */
    crawlerHits: countSchema,
});
export const ozetSalesSchema = z.strictObject({
    currency: z.string().length(3),
    last30: z.strictObject({ orders: countSchema, revenueCents: countSchema }),
    /** Orders per 30 days over the 335 days before them (the Etsy read keeps 365 days); one decimal. */
    before: z.strictObject({ days: countSchema, orders: countSchema, revenueCents: countSchema }),
});
export const ozetOutreachSchema = z.strictObject({
    drafts: countSchema,
    /** Of the drafts, those to a business's generic address (info@, sales@), the only ones the first waves send to. */
    genericDrafts: countSchema,
    sent: countSchema,
    replies: countSchema,
    bounced: countSchema,
    firstSentAt: isoDateTimeSchema.nullable(),
});
export const ozetTeamSchema = z.strictObject({
    genesisSpentMicroUsd: countSchema,
    genesisCapMicroUsd: countSchema,
    /** The Genel Müdür's row: active, paused (and why, since when) or disabled. */
    gmStatus: z.string().min(1).max(32),
    gmStatusReason: z.string().max(64).nullable(),
    gmStatusSince: isoDateTimeSchema.nullable(),
    gmLastRunAt: isoDateTimeSchema.nullable(),
});
export const ozetViewSchema = z.strictObject({
    generatedAt: isoDateTimeSchema,
    launchDay: isoDateSchema,
    decisions: z.strictObject({ open: countSchema, items: z.array(ozetDecisionSchema).max(OZET_DECISION_LIMIT) }),
    search: z.array(ozetSearchSchema),
    visits: ozetVisitsSchema,
    sales: ozetSalesSchema,
    outreach: ozetOutreachSchema,
    team: ozetTeamSchema,
});
/*
 * Özet panosu (v0.1.257, specs/ozet-pano.md; Sercan approved the design on 2 Nov 2026): GET /ozet/summary?period=.
 * One period (today, this week, this month, the last 30 days) against the one before it, in the report time zone
 * (Europe/Istanbul): the totals of both, one row per day from the start of the previous period to today, the last
 * eight Monday weeks, the state of each source and the weekly AI visibility check. Counts only: no address, handle,
 * message text or external reference. A source with nothing stored for a day reads as null, never as 0; what no reader
 * stores at all is named in notMeasured.
 */
export const OZET_PERIODS = ['day', 'week', 'month', 'd30'];
/** GET /ozet/summary without ?period= reads this week. */
export const OZET_DEFAULT_PERIOD = 'week';
/** The week table covers this many Monday weeks, the running one last. */
export const OZET_SUMMARY_WEEKS = 8;
/** The site assistant's chats are deleted after this many days (migration 0103), so older days read null. */
export const OZET_SITE_CHAT_KEEP_DAYS = 90;
/** What no reader stores, so the page says "ölçülmüyor" instead of a number. */
export const OZET_NOT_MEASURED = ['outreach_opens', 'site_orders', 'etsy_daily_visits', 'cgtrader_sales'];
export const ozetSummaryQuerySchema = z.strictObject({ period: z.enum(OZET_PERIODS).optional() });
/** Days in the report time zone, both ends included. */
export const ozetRangeSchema = z.strictObject({ from: isoDateSchema, to: isoDateSchema, days: countSchema });
const ozetSourceSessionsSchema = z.partialRecord(z.enum(SITE_SOURCE_GROUPS), countSchema);
export const ozetSearchTotalsSchema = z.strictObject({
    clicks: countSchema,
    impressions: countSchema,
    /** Impression weighted average position; null when the engine gives none. */
    position: z.number().nonnegative().nullable(),
    /** The last day of the range the engine has reported (its own day; Pacific Time for Google). */
    dataThrough: isoDateSchema.nullable(),
});
export const ozetSummaryTotalsSchema = z.strictObject({
    sales: z.strictObject({
        /** Etsy orders that count (not cancelled or refunded) and their totals. */
        orders: countSchema,
        revenueCents: countSchema,
        /** buyer.contribution over the same range: net, estimated fees, ads and what is left. */
        netCents: z.number().int(),
        feesCents: z.number().int(),
        adCents: z.number().int(),
        contributionCents: z.number().int(),
    }),
    site: z.strictObject({
        /** The server log; null when no day of the range has been read. */
        entries: countSchema.nullable(),
        pageViews: countSchema.nullable(),
        aiEntries: countSchema.nullable(),
        outreachEntries: countSchema.nullable(),
        crawlerHits: countSchema.nullable(),
        /** AI services opening a page live for a person's question, and reading it for their index. */
        aiLiveFetches: countSchema.nullable(),
        aiIndexFetches: countSchema.nullable(),
        /** Clicks on the site's Etsy links. */
        etsyClicks: countSchema.nullable(),
        /** GA4 sessions; null when GA4 has no day in the range. */
        sessions: countSchema.nullable(),
        sourceSessions: ozetSourceSessionsSchema,
    }),
    google: ozetSearchTotalsSchema.nullable(),
    bing: ozetSearchTotalsSchema.nullable(),
    inbox: z.strictObject({
        /** New customer conversations by channel: Gmail, the site's forms, Etsy (screenshots and Etsy mails), other. */
        gmail: countSchema,
        siteForm: countSchema,
        etsy: countSchema,
        other: countSchema,
        messagesIn: countSchema,
        repliesOut: countSchema,
    }),
    /** The site assistant; null when the whole range is older than the 90 days the chats are kept. */
    siteChat: z.strictObject({ chats: countSchema, turns: countSchema, handoffs: countSchema }).nullable(),
    outreach: z.strictObject({
        sent: countSchema,
        bounced: countSchema,
        /** Replies by a person (auto replies and bounces left out), and the interested ones among them. */
        replies: countSchema,
        interested: countSchema,
        /** Firms whose mail link a person (not a scanner) opened in the range. */
        clickFirms: countSchema,
    }),
    team: z.strictObject({
        tasksDone: countSchema,
        approvalsDecided: countSchema,
        approvalsApproved: countSchema,
        approvalsRejected: countSchema,
        /** Money spent on AI and services in the range (notional costs left out). */
        costMicroUsd: countSchema,
    }),
    /**
     * YouTube views gained over the range (daily snapshots); null without a snapshot at both ends or when the newest
     * snapshot is older than the range (v0.1.266).
     */
    youtubeViews: countSchema.nullable(),
    /** Pinterest impressions of the range's days (v0.1.266, the analytics' daily numbers); null when no day has any. */
    pinterestImpressions: countSchema.nullable(),
});
export const ozetSummaryDaySchema = z.strictObject({
    day: isoDateSchema,
    orders: countSchema,
    revenueCents: countSchema,
    /** The server log of the day; null when the day has not been read. */
    entries: countSchema.nullable(),
    pageViews: countSchema.nullable(),
    aiEntries: countSchema.nullable(),
    outreachEntries: countSchema.nullable(),
    crawlerHits: countSchema.nullable(),
    etsyClicks: countSchema.nullable(),
    /** GA4: null when GA4 has no row for the day (it comes the next morning). */
    sessions: countSchema.nullable(),
    sourceSessions: ozetSourceSessionsSchema,
    googleClicks: countSchema.nullable(),
    googleImpressions: countSchema.nullable(),
    bingClicks: countSchema.nullable(),
    bingImpressions: countSchema.nullable(),
    /** New customer conversations, and of them the ones from the site's forms. */
    conversations: countSchema,
    siteForms: countSchema,
    messagesIn: countSchema,
    repliesOut: countSchema,
    /** Null for days older than the 90 days the site assistant keeps. */
    siteChats: countSchema.nullable(),
    outreachSent: countSchema,
    outreachReplies: countSchema,
    tasksDone: countSchema,
    approvalsDecided: countSchema,
    costMicroUsd: countSchema,
    /** YouTube views gained that day; null without a snapshot for the day and the one before. */
    youtubeViews: countSchema.nullable(),
    /**
     * Pinterest impressions of the day (v0.1.266, shop.channel_metric_days); null when Pinterest has given none for it
     * yet (it completes a day late). pinterestEstimated: Pinterest still reports the day as an estimate.
     */
    pinterestImpressions: countSchema.nullable(),
    pinterestEstimated: z.boolean(),
});
export const ozetSummaryWeekSchema = z.strictObject({
    /** Monday. */
    weekStart: isoDateSchema,
    /** Sunday, or today for the running week. */
    to: isoDateSchema,
    running: z.boolean(),
    entries: countSchema.nullable(),
    aiEntries: countSchema.nullable(),
    sessions: countSchema.nullable(),
    /** Search Console's Monday weeks (as on SEO); null when the engine has no day in the week. */
    googleClicks: countSchema.nullable(),
    googleImpressions: countSchema.nullable(),
    orders: countSchema,
    revenueCents: countSchema,
    messagesIn: countSchema,
    repliesOut: countSchema,
    outreachSent: countSchema,
});
export const OZET_SUMMARY_SOURCES = [
    'etsy',
    'server_log',
    'ga4',
    'search_console',
    'bing',
    'youtube',
    'pinterest',
    'cgtrader',
];
export const ozetSummarySourceSchema = z.strictObject({
    id: z.enum(OZET_SUMMARY_SOURCES),
    state: z.enum(['ok', 'not_connected', 'error', 'waiting', 'no_data']),
    /** The last successful read. */
    readAt: isoDateTimeSchema.nullable(),
    /** The last day the source has data for, when it reports one. */
    dataThrough: isoDateSchema.nullable(),
});
export const ozetVisibilityWeekSchema = z.strictObject({
    weekStart: isoDateSchema,
    engine: z.enum(SEO_ENGINES),
    asked: countSchema,
    found: countSchema,
    errors: countSchema,
});
export const ozetSocialSchema = z.strictObject({
    /** The newest daily snapshot of each channel; null before the first read. */
    youtube: z
        .strictObject({ day: isoDateSchema, subscribers: countSchema.nullable(), views: countSchema.nullable() })
        .nullable(),
    pinterest: z
        .strictObject({
        day: isoDateSchema,
        followers: countSchema.nullable(),
        impressions30d: countSchema.nullable(),
        outboundClicks30d: countSchema.nullable(),
    })
        .nullable(),
    cgtrader: z.strictObject({ day: isoDateSchema, models: countSchema.nullable() }).nullable(),
});
export const ozetSummaryViewSchema = z.strictObject({
    generatedAt: isoDateTimeSchema,
    timeZone: z.string().min(1).max(64),
    period: z.enum(OZET_PERIODS),
    today: isoDateSchema,
    current: ozetRangeSchema,
    previous: ozetRangeSchema,
    /**
     * For the late sources (GA4, Google, Bing) `previous` is counted over as many days as `current` has data for, so a
     * source that is 2-3 days behind does not read as a drop.
     */
    totals: z.strictObject({ current: ozetSummaryTotalsSchema, previous: ozetSummaryTotalsSchema }),
    /** One row per day from previous.from to current.to, oldest first. */
    daily: z.array(ozetSummaryDaySchema).max(70),
    /** The last OZET_SUMMARY_WEEKS Monday weeks, oldest first, the running one last. */
    weekly: z.array(ozetSummaryWeekSchema).max(OZET_SUMMARY_WEEKS),
    sources: z.array(ozetSummarySourceSchema).max(OZET_SUMMARY_SOURCES.length),
    aiVisibility: z.array(ozetVisibilityWeekSchema),
    social: ozetSocialSchema,
    notMeasured: z.array(z.enum(OZET_NOT_MEASURED)),
});
function ozetAddDays(day, days) {
    const at = new Date(`${day}T00:00:00.000Z`);
    at.setUTCDate(at.getUTCDate() + days);
    return at.toISOString().slice(0, 10);
}
/** Days between two IsoDates, both ends included. */
function ozetDaysOf(from, to) {
    return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
}
function ozetRange(from, to) {
    return { from, to, days: ozetDaysOf(from, to) };
}
/** Monday of the week of an IsoDate. */
export function ozetMondayOf(day) {
    const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
    return ozetAddDays(day, -((weekday + 6) % 7));
}
/**
 * A period and the one it is compared with; `today` is the report time zone's day. Day: today against yesterday.
 * Week: Monday to today against the same weekdays of last week. Month: the 1st to today against the same days of last
 * month, a shorter month ending at its last day (31 Mar against 1-28 Feb). d30: the last 30 days against the 30 before.
 */
export function ozetSummaryRanges(today, period) {
    if (period === 'day') {
        const yesterday = ozetAddDays(today, -1);
        return { current: ozetRange(today, today), previous: ozetRange(yesterday, yesterday) };
    }
    if (period === 'week') {
        const monday = ozetMondayOf(today);
        return {
            current: ozetRange(monday, today),
            previous: ozetRange(ozetAddDays(monday, -7), ozetAddDays(today, -7)),
        };
    }
    if (period === 'month') {
        const first = `${today.slice(0, 8)}01`;
        const before = new Date(`${first}T00:00:00Z`);
        before.setUTCMonth(before.getUTCMonth() - 1);
        const previousFirst = before.toISOString().slice(0, 10);
        const sameDay = ozetAddDays(previousFirst, ozetDaysOf(first, today) - 1);
        const end = new Date(`${previousFirst}T00:00:00Z`);
        end.setUTCMonth(end.getUTCMonth() + 1, 0);
        const previousLast = end.toISOString().slice(0, 10);
        return {
            current: ozetRange(first, today),
            previous: ozetRange(previousFirst, sameDay < previousLast ? sameDay : previousLast),
        };
    }
    return {
        current: ozetRange(ozetAddDays(today, -29), today),
        previous: ozetRange(ozetAddDays(today, -59), ozetAddDays(today, -30)),
    };
}
//# sourceMappingURL=ozet.js.map